import { createRouteMatcher } from './matcher';
import type { PreparedRoute, RouteCoreContract, RouteDefinition, RouteMatch, RouteSnapshot } from './types';
import { RouteRedirect } from './types';
import { signal } from '@preact/signals-core';
import { resolvePath, toLocation } from 'src/lib/navigation/path';
import type { AppLocation } from 'src/lib/navigation/types';

const MAX_REDIRECTS = 16;

const abortError = () => new DOMException('The route entry was superseded', 'AbortError');

const throwIfAborted = (abortSignal: AbortSignal) => {
    if (abortSignal.aborted) throw abortError();
};

interface EnteredMatch<TContainer, TLoaded> {
    readonly match: RouteMatch<TContainer, TLoaded>;
    readonly disposables: DisposableStack;
}

/** Settles an entry's `committed` promise: resolved when its navigation commits, rejected if it never will. */
class EntryCommit {
    readonly promise: Promise<void>;
    #resolve!: () => void;
    #reject!: (reason: unknown) => void;

    constructor() {
        this.promise = new Promise<void>((resolve, reject) => {
            this.#resolve = resolve;
            this.#reject = reject;
        });
        // Entries that never await commit must not surface an unhandled rejection when abandoned.
        this.promise.catch(() => undefined);
    }

    commit(): void {
        this.#resolve();
    }

    abandon(): void {
        this.#reject(abortError());
    }
}

export class RouteCore<TContainer, TLoaded> implements RouteCoreContract<TContainer, TLoaded> {
    readonly current = signal<RouteSnapshot<TContainer, TLoaded> | null>(null);
    readonly pendingLocation = signal<AppLocation | null>(null);
    readonly #container: TContainer;
    readonly #match;
    #active: readonly EnteredMatch<TContainer, TLoaded>[] = [];
    #pendingCount = 0;

    constructor(definitions: readonly RouteDefinition<TContainer, TLoaded>[], container: TContainer) {
        this.#container = container;
        this.#match = createRouteMatcher(definitions);
    }

    async prepare(initialLocation: AppLocation, abortSignal: AbortSignal): Promise<PreparedRoute<TContainer, TLoaded>> {
        const pendingId = ++this.#pendingCount;
        this.pendingLocation.value = initialLocation;
        let location = initialLocation;
        let replace = false;

        try {
            for (let redirects = 0; redirects <= MAX_REDIRECTS; redirects += 1) {
                throwIfAborted(abortSignal);
                try {
                    const { entered, entryCommit } = await this.#enter(location, abortSignal);
                    const snapshot: RouteSnapshot<TContainer, TLoaded> = {
                        location,
                        matches: entered.map(({ match }) => match),
                        params: entered.at(-1)?.match.params ?? {},
                    };
                    let settled = false;

                    return {
                        location,
                        replace,
                        snapshot,
                        commit: (committedLocation = location) => {
                            if (settled) return;
                            settled = true;
                            if (pendingId !== this.#pendingCount) {
                                this.#disposeEntered(entered);
                                entryCommit.abandon();
                                return;
                            }
                            this.#disposeActive();
                            this.#active = entered;
                            this.current.value =
                                committedLocation === location
                                    ? snapshot
                                    : { ...snapshot, location: committedLocation };
                            this.pendingLocation.value = null;
                            entryCommit.commit();
                        },
                        [Symbol.dispose]: () => {
                            if (settled) return;
                            settled = true;
                            this.#disposeEntered(entered);
                            entryCommit.abandon();
                            if (pendingId === this.#pendingCount) this.pendingLocation.value = null;
                        },
                    };
                } catch (error) {
                    if (!(error instanceof RouteRedirect)) throw error;
                    replace ||= error.replace;
                    location = toLocation(resolvePath(error.to, location.pathname), location);
                    if (redirects === MAX_REDIRECTS) {
                        throw new Error(`Route redirect limit of ${MAX_REDIRECTS} exceeded`);
                    }
                }
            }

            throw new Error('Unreachable route redirect state');
        } catch (error) {
            if (pendingId === this.#pendingCount) this.pendingLocation.value = null;
            throw error;
        }
    }

    async #enter(
        location: AppLocation,
        abortSignal: AbortSignal,
    ): Promise<{ entered: EnteredMatch<TContainer, TLoaded>[]; entryCommit: EntryCommit }> {
        const branch = this.#match(location.pathname);
        const entered: EnteredMatch<TContainer, TLoaded>[] = [];
        const entryCommit = new EntryCommit();

        try {
            for (const [index, definition] of branch.definitions.entries()) {
                throwIfAborted(abortSignal);
                const disposables = new DisposableStack();
                try {
                    const [loaded] = await Promise.all([
                        definition.load?.(),
                        definition.enter?.({
                            container: this.#container,
                            location,
                            params: branch.params,
                            signal: abortSignal,
                            committed: entryCommit.promise,
                            register(disposable) {
                                if (abortSignal.aborted) {
                                    disposable[Symbol.dispose]();
                                    throw abortError();
                                }
                                disposables.use(disposable);
                            },
                        }),
                    ]);
                    throwIfAborted(abortSignal);
                    entered.push({
                        disposables,
                        match: {
                            definition,
                            loaded: loaded ?? null,
                            params: branch.params,
                            pathnameBase: branch.pathnameBases[index]!,
                        },
                    });
                } catch (error) {
                    disposables[Symbol.dispose]();
                    throw error;
                }
            }
            return { entered, entryCommit };
        } catch (error) {
            this.#disposeEntered(entered);
            entryCommit.abandon();
            throw error;
        }
    }

    #disposeEntered(entered: readonly EnteredMatch<TContainer, TLoaded>[]): void {
        for (const { disposables } of entered.toReversed()) {
            disposables[Symbol.dispose]();
        }
    }

    #disposeActive(): void {
        this.#disposeEntered(this.#active);
        this.#active = [];
    }

    [Symbol.dispose](): void {
        this.#pendingCount += 1;
        this.pendingLocation.value = null;
        this.#disposeActive();
        this.current.value = null;
    }
}
